// Enhanced Solana Wallet Logging Service with Server Results
import { LoggingService } from './logging.service';
import { LogAction } from '../types/logging.types';
import axios from 'axios';
import tokenService from 'services/token.service';

interface SolanaConnectionResult {
  success: boolean;
  walletAddress?: string;
  error?: string;
  connectionTime?: number;
  walletName?: string;
}

interface SolanaAssociationResult {
  success: boolean;
  evmAddress?: string;
  solanaAddress?: string;
  error?: string;
  associationId?: string;
  createdAt?: string;
}

interface ServerLogResponse {
  success: boolean;
  logId?: string;
  error?: string;
}

export class SolanaLoggingService {
  private logger: LoggingService;
  private readonly API_BASE: string;

  constructor(logger: LoggingService) {
    this.logger = logger;
    this.API_BASE = process.env.REACT_APP_API_SECONDARY || '';
  }

  // Log Solana wallet connection attempt with server notification
  public async logSolanaConnectionAttempt(data: {
    evmAddress?: string;
    isAuth: boolean;
    availableWallets: number;
    walletNames: string[];
    userAgent?: string;
  }): Promise<void> {
    // Log to local logging system
    this.logger.logConnectionAttempt('solana', data);

    // Send server notification for analytics
    try {
      await this.sendServerLog({
        event: 'solana_connection_attempt',
        data: {
          ...data,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId()
        }
      });
    } catch (error) {
      console.warn('Failed to send server log for connection attempt:', error);
    }
  }

  // Log Solana wallet connection success with server result tracking
  public async logSolanaConnectionSuccess(result: SolanaConnectionResult): Promise<void> {
    // Log to local logging system
    this.logger.logConnectionSuccess('solana', result.walletAddress || '', {
      connectionTime: result.connectionTime,
      walletName: result.walletName
    });

    // Send server notification with connection result
    try {
      await this.sendServerLog({
        event: 'solana_connection_success',
        data: {
          ...result,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId()
        }
      });

      // Track connection success in server analytics
      await this.trackConnectionMetrics('success', result);
    } catch (error) {
      console.warn('Failed to send server log for connection success:', error);
    }
  }

  // Log Solana wallet connection error with detailed server tracking
  public async logSolanaConnectionError(error: Error, context: {
    evmAddress?: string;
    availableWallets: number;
    timeoutDuration?: number;
    walletName?: string;
  }): Promise<void> {
    // Log to local logging system
    this.logger.logConnectionError('solana', error, context);

    // Send detailed error report to server
    try {
      await this.sendServerLog({
        event: 'solana_connection_error',
        data: {
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack
          },
          context,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId()
        }
      });

      // Track connection failure metrics
      await this.trackConnectionMetrics('error', { error: error.message });
    } catch (serverError) {
      console.warn('Failed to send server log for connection error:', serverError);
    }
  }

  // Log Solana wallet association with server result verification
  public async logSolanaAssociationAttempt(data: {
    evmAddress: string;
    solanaAddress: string;
    isAutoAssociation?: boolean;
  }): Promise<void> {
    // Log to local logging system
    this.logger.logWalletConnection('solana', LogAction.ASSOCIATION_ATTEMPT, data);

    // Send server notification
    try {
      await this.sendServerLog({
        event: 'solana_association_attempt',
        data: {
          ...data,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId()
        }
      });
    } catch (error) {
      console.warn('Failed to send server log for association attempt:', error);
    }
  }

  // Log Solana wallet association success with server verification
  public async logSolanaAssociationSuccess(result: SolanaAssociationResult): Promise<void> {
    // Log to local logging system
    this.logger.logConnectionSuccess('solana', result.solanaAddress || '', {
      action: 'association_success',
      evmAddress: result.evmAddress,
      associationId: result.associationId
    });

    // Send server notification and verify association
    try {
      await this.sendServerLog({
        event: 'solana_association_success',
        data: {
          ...result,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId()
        }
      });

      // Verify association on server side
      if (result.evmAddress && result.solanaAddress) {
        await this.verifyAssociationOnServer(result.evmAddress, result.solanaAddress);
      }

    } catch (error) {
      console.warn('Failed to send server log for association success:', error);
    }
  }

  // Log Solana wallet association error with server notification
  public async logSolanaAssociationError(error: Error, context: {
    evmAddress?: string;
    solanaAddress?: string;
    statusCode?: number;
    phase: string;
  }): Promise<void> {
    // Log to local logging system
    this.logger.logConnectionError('solana', error, context);

    // Send detailed error report to server
    try {
      await this.sendServerLog({
        event: 'solana_association_error',
        data: {
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack
          },
          context,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId()
        }
      });
    } catch (serverError) {
      console.warn('Failed to send server log for association error:', serverError);
    }
  }

  // Log EVM purchase actions with Solana wallet association context
  public async logSolanaWalletPurchase(action: string, data: {
    idoId: string;
    amount: string;
    currency: string;
    evmAddress?: string;
    solanaAddress?: string;
    isAssociated: boolean;
    txHash?: string;
    error?: Error;
    purchaseMethod?: string;
    solanaRequirement?: boolean;
  }): Promise<void> {
    // Log to local logging system based on action - EVM purchases with Solana association context
    if (action.includes('purchase_attempt')) {
      this.logger.logPurchaseAttempt(data.idoId, data.amount, data.currency, {
        solanaWalletAddress: data.solanaAddress,
        solanaWalletAssociated: data.isAssociated,
        purchaseMethod: data.purchaseMethod || 'EVM',
        requiresSolanaAssociation: data.solanaRequirement || false
      });
    } else if (action.includes('purchase_success')) {
      this.logger.logPurchaseSuccess(data.idoId, data.amount, data.currency, data.txHash || '', {
        solanaWalletAddress: data.solanaAddress,
        solanaWalletAssociated: data.isAssociated,
        purchaseMethod: data.purchaseMethod || 'EVM',
        requiresSolanaAssociation: data.solanaRequirement || false
      });
    } else if (action.includes('purchase_error') && data.error) {
      this.logger.logPurchaseError(data.idoId, data.amount, data.currency, data.error, {
        solanaWalletAddress: data.solanaAddress,
        solanaWalletAssociated: data.isAssociated,
        purchaseMethod: data.purchaseMethod || 'EVM',
        requiresSolanaAssociation: data.solanaRequirement || false
      });
    }

    // Send enhanced server notification for EVM purchases with Solana context
    try {
      await this.sendServerLog({
        event: `evm_purchase_solana_context_${action.replace('evm_purchase_with_solana_context_', '')}`,
        data: {
          ...data,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId(),
          clarification: 'Purchase executed via EVM wallet, Solana wallet used only for association verification'
        }
      });
    } catch (error) {
      console.warn(`Failed to send server log for ${action}:`, error);
    }
  }

  // Verify association status on server
  private async verifyAssociationOnServer(evmAddress: string, solanaAddress: string): Promise<boolean> {
    try {
      const token = tokenService.tokens?.accessToken;
      if (!token) return false;

      const response = await axios.get(
        `${this.API_BASE}/wallet/association/${evmAddress}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        }
      );

      const isVerified = response.data.isAssociated && 
                        response.data.solanaAddress === solanaAddress;

      // Log verification result
      await this.sendServerLog({
        event: 'solana_association_verification',
        data: {
          evmAddress,
          solanaAddress,
          isVerified,
          serverData: response.data,
          timestamp: new Date().toISOString()
        }
      });

      return isVerified;
    } catch (error) {
      console.warn('Failed to verify association on server:', error);
      return false;
    }
  }

  // Track connection metrics on server
  private async trackConnectionMetrics(result: 'success' | 'error', data: any): Promise<void> {
    try {
      await this.sendServerLog({
        event: 'solana_connection_metrics',
        data: {
          result,
          metrics: data,
          timestamp: new Date().toISOString(),
          sessionId: this.logger.getSessionId(),
          userAgent: navigator.userAgent
        }
      });
    } catch (error) {
      console.warn('Failed to track connection metrics:', error);
    }
  }

  // Send log to server with enhanced error handling
  private async sendServerLog(logData: {
    event: string;
    data: any;
  }): Promise<ServerLogResponse> {
    try {
      const token = tokenService.tokens?.accessToken;
      
      const response = await axios.post(
        `${this.API_BASE}/logs/solana`,
        {
          ...logData,
          clientTimestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          },
          timeout: 5000
        }
      );

      return {
        success: true,
        logId: response.data?.logId
      };
    } catch (error: any) {
      // Return error but don't throw to prevent blocking UI
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get connection statistics from server
  public async getConnectionStatistics(): Promise<{
    totalConnections: number;
    successRate: number;
    commonErrors: string[];
    avgConnectionTime: number;
  } | null> {
    try {
      const token = tokenService.tokens?.accessToken;
      if (!token) return null;

      const response = await axios.get(
        `${this.API_BASE}/logs/solana/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        }
      );

      return response.data;
    } catch (error) {
      console.warn('Failed to get connection statistics:', error);
      return null;
    }
  }

  // Force flush all pending logs
  public async flushLogs(): Promise<void> {
    await this.logger.flush();
  }
}

// Singleton instance
let solanaLoggingInstance: SolanaLoggingService | null = null;

export const getSolanaLoggingService = (logger?: LoggingService): SolanaLoggingService => {
  if (!solanaLoggingInstance) {
    const loggingService = logger || require('./logging.service').getLoggingService();
    solanaLoggingInstance = new SolanaLoggingService(loggingService);
  }
  return solanaLoggingInstance;
};